import React, { useState, useMemo } from 'react';
import { 
  Card, 
  CardHeader, 
  CardContent, 
  Box, 
  Grid, 
  TextField, 
  MenuItem, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Typography,
  Divider,
  Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PacingInvestmentItem from '../PacingInvestmentItem/PacingInvestmentItem';
import _ from 'lodash';

const PacingInvestmentItems = ({
  fundDataMap,
  portfolioInformation,
  fundInformation,
  historicData,
  futureCashflows,
  pacingData,
  scenarios,
  onFundInfoUpdate,
  onPacingDataUpdate,
}) => {
  // State for expanded groups
  const [expandedGroup, setExpandedGroup] = useState(null);

  // Grouping & Sorting Controls (shown first row)
  const [groupBy, setGroupBy] = useState('Manager');     // Default
  const [sortBy, setSortBy] = useState('marketValueBase'); 
  const [sortOrder, setSortOrder] = useState('desc');   

  // Filters (second row)
  const [fundStatusFilter, setFundStatusFilter] = useState('All');
  const [vintageFilter, setVintageFilter] = useState(0);
  const [managerFilter, setManagerFilter] = useState('');
  const [commitmentFilter, setCommitmentFilter] = useState(0);

  const asOfDate = portfolioInformation?.asOfDate || '2024-03-31';
  const fundNames = Object.keys(fundDataMap || {});

  // Build a normalized fund list with all needed fields
  const fundList = useMemo(() => {
    return fundNames.map(fundName => {
      const localPacingData = pacingData[fundName] || {};
      const pacingRow = localPacingData[asOfDate] || {};
      const info = fundInformation[fundName] || {};
      
      const marketValueBase = pacingRow.marketValueBase || 0;
      const fundStatus = info.fundStatus || 'N/A'; 
      const vintageYear = info.vintage || 0;
      const manager = info.manager || 'Unknown';
      const commitmentLocal = info.commitmentLocal || 0;
      const strategy = info.strategy || 'Unknown';
      const currency = info.localCurrency || 'Unknown';
      
      return { 
        fundName, 
        fundStatus, 
        vintageYear, 
        marketValueBase,
        manager,
        commitmentLocal,
        strategy,
        currency,
      };
    });
  }, [fundNames, fundInformation, pacingData, asOfDate]);

  const processedFunds = useMemo(() => {
    let filtered = [...fundList];

    // Apply filters
    if (fundStatusFilter !== 'All') {
      filtered = filtered.filter(f => f.fundStatus === fundStatusFilter);
    }

    if (vintageFilter > 0) {
      filtered = filtered.filter(f => f.vintageYear > vintageFilter);
    }

    if (managerFilter.trim() !== '') {
      const mf = managerFilter.trim().toLowerCase();
      filtered = filtered.filter(f => f.manager.toLowerCase().includes(mf));
    }

    if (commitmentFilter > 0) {
      filtered = filtered.filter(f => f.commitmentLocal > commitmentFilter);
    }

    // Sort function
    const sortFn = (a, b) => {
      let valA = a[sortBy] || 0;
      let valB = b[sortBy] || 0;
      if (sortOrder === 'asc') return valA - valB;
      return valB - valA;
    };

    // Determine grouping key
    const getGroupKey = (f) => {
      switch (groupBy) {
        case 'Manager':
          return f.manager;
        case 'Strategy':
          return f.strategy;
        case 'Vintage':
          return f.vintageYear;
        case 'fundStatus':
          return f.fundStatus;
        case 'currency':
          return f.currency;
        default:
          return 'NoGroup';
      }
    };

    if (groupBy !== 'None') {
      const grouped = _.groupBy(filtered, getGroupKey);

      // Compute aggregate per group for sorting groups
      const groupData = Object.entries(grouped).map(([groupVal, funds]) => {
        const aggregatedValue = _.sumBy(funds, (f) => f[sortBy] || 0);
        // Sort funds within group
        funds.sort(sortFn);
        return {
          groupName: groupVal,
          funds,
          aggregatedValue
        };
      });

      // Sort groups by aggregated metric
      groupData.sort((a, b) => {
        if (sortOrder === 'asc') return a.aggregatedValue - b.aggregatedValue;
        return b.aggregatedValue - a.aggregatedValue;
      });

      return groupData;
    } else {
      // Ungrouped
      filtered.sort(sortFn);
      return filtered;
    }
  }, [fundList, fundStatusFilter, vintageFilter, managerFilter, commitmentFilter, sortBy, sortOrder, groupBy]);

  const handleGroupToggle = (groupName) => {
    setExpandedGroup((prev) => (prev === groupName ? null : groupName));
  };

  const renderGrouped = () => {
    return processedFunds.map((group) => {
      const { groupName, funds, aggregatedValue } = group;
      let groupLabel = '';
      switch (groupBy) {
        case 'Manager':
          groupLabel = `Manager: ${groupName}`;
          break;
        case 'Strategy':
          groupLabel = `Strategy: ${groupName}`;
          break;
        case 'Vintage':
          groupLabel = `Vintage Year: ${groupName}`;
          break;
        case 'fundStatus':
          groupLabel = `Fund Status: ${groupName}`;
          break;
        case 'currency':
          groupLabel = `Currency: ${groupName}`;
          break;
        default:
          groupLabel = `${groupName}`;
      }

      const valueLabel = (sortBy === 'marketValueBase')
        ? 'Total Market Value'
        : (sortBy === 'commitmentLocal')
          ? 'Total Commitment'
          : 'Total Value';

      return (
        <Accordion 
          key={groupName}
          expanded={expandedGroup === groupName}
          onChange={() => handleGroupToggle(groupName)}
          sx={{
            mb: 2,
            '&:before': { display: 'none' },
            boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
              bgcolor: '#f9f9f9',
              border: '1px solid #ddd'

          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${groupName}-content`}
            id={`${groupName}-header`}
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ flex: 1 }}>
              {groupLabel}
            </Typography>
            <Typography variant="subtitle1" sx={{ ml: 2 }}>
              {valueLabel}: {aggregatedValue.toLocaleString()}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3} direction="column">
              {funds.map(({ fundName }) => (
                <Grid item xs={12} key={fundName}>
                  <PacingInvestmentItem
                    fundName={fundName}
                    portfolioInformation={portfolioInformation}
                    initialFundInfo={fundInformation[fundName]}
                    historicData={historicData[fundName]}
                    futureCashflows={futureCashflows[fundName]}
                    pacingData={pacingData[fundName]}
                    scenarios={scenarios}
                    onPacingDataUpdate={onPacingDataUpdate}
                    onFundInfoUpdate={onFundInfoUpdate}
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  const renderUngrouped = () => {
    return (
      <Grid container spacing={3} direction="column">
        {processedFunds.map(({ fundName }) => (
          <Grid item xs={12} key={fundName}>
            <PacingInvestmentItem
              fundName={fundName}
              portfolioInformation={portfolioInformation}
              initialFundInfo={fundInformation[fundName]}
              historicData={historicData[fundName]}
              futureCashflows={futureCashflows[fundName]}
              pacingData={pacingData[fundName]}
              onPacingDataUpdate={onPacingDataUpdate}
              onFundInfoUpdate={onFundInfoUpdate}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Card sx={{ mt: 4 }}>
      <CardHeader title="Fund-Level Pacing Projections" />
      <CardContent>

        {/* Top Row: Group By, Sort By, Sort Order */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          {/* Group By */}
          <TextField
            select
            label="Group By"
            variant="outlined"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="None">None</MenuItem>
            <MenuItem value="Manager">Manager</MenuItem>
            <MenuItem value="Strategy">Strategy</MenuItem>
            <MenuItem value="Vintage">Vintage</MenuItem>
            <MenuItem value="fundStatus">Fund Status</MenuItem>
            <MenuItem value="currency">Currency</MenuItem>
          </TextField>

          {/* Sort Controls combined visually */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              select
              label="Sort By"
              variant="outlined"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="marketValueBase">Market Value</MenuItem>
              <MenuItem value="commitmentLocal">Commitment</MenuItem>
              <MenuItem value="vintageYear">Vintage Year</MenuItem>
            </TextField>

            <TextField
              select
              label="Order"
              variant="outlined"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="asc">Asc</MenuItem>
              <MenuItem value="desc">Desc</MenuItem>
            </TextField>
          </Box>
        </Box>

        {/* Second Row: Filters */}
        <Box 
          sx={{ 
            position: 'relative',
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 2, 
            mb: 2,
            bgcolor: '#f9f9f9',
            p: 2,
            borderRadius: 1,
            border: '1px solid #ddd'
          }}
        >
          <Typography
            variant="caption"
            component="span"
            sx={{
              position: 'absolute',
              top: -8,
              left: 10,
              px: 0.5,
              bgcolor: '#f9f9f9',
              color: 'rgba(0, 0, 0, 0.6)',
              fontSize: '0.75rem',
              lineHeight: 1
            }}
          >
            Filters
          </Typography>
          
          {/* Fund Status Filter */}
          <TextField
            select
            label="Fund Status"
            variant="outlined"
            value={fundStatusFilter}
            onChange={(e) => setFundStatusFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Current">Current</MenuItem>
            <MenuItem value="Future Fund">Future Fund</MenuItem>
          </TextField>

          {/* Vintage Filter */}
          <TextField
            label="Vintage > Year"
            type="number"
            variant="outlined"
            value={vintageFilter}
            onChange={(e) => setVintageFilter(Number(e.target.value))}
            sx={{ width: 180 }}
          />

          {/* Manager Filter */}
          <TextField
            label="Manager Contains"
            type="text"
            variant="outlined"
            value={managerFilter}
            onChange={(e) => setManagerFilter(e.target.value)}
            sx={{ width: 180 }}
          />

          {/* Commitment Filter */}
          <TextField
            label="Commitment >"
            type="number"
            variant="outlined"
            value={commitmentFilter}
            onChange={(e) => setCommitmentFilter(Number(e.target.value))}
            sx={{ width: 180 }}
          />
        </Box>

        {/* Divider to differentiate filters */}
        <Divider sx={{ my: 2 }} />

        <Box sx={{ my: 4 }}>
          {Array.isArray(processedFunds) && processedFunds.length > 0 && typeof processedFunds[0] === 'object' && processedFunds[0].hasOwnProperty('funds')
            ? renderGrouped()
            : renderUngrouped()
          }
        </Box>
      </CardContent>
    </Card>
  );
};

export default React.memo(PacingInvestmentItems);